import { isAxiosError } from "axios";

import { EAutomationRunFamily } from "../constants/automation";
import type { EContentReportReason } from "../types/content-report";
import {
  CONTENT_REPORT_ERROR_CODES,
  CONTENT_REPORT_REASONS,
  EContentReportTargetKind,
} from "../types/content-report";

// NOTE: NSAI-6374 — an agent run lives in one of three tables, so it reports as
// one of three kinds. Shared because web and mobile both render the same run
// card and must not disagree about which kind a run is; a wrong kind here is a
// 404 the reporter cannot explain.
export const AUTOMATION_RUN_FAMILY_TO_REPORT_KIND: Record<
  EAutomationRunFamily,
  EContentReportTargetKind
> = {
  [EAutomationRunFamily.CRON]: EContentReportTargetKind.CRON_RUN,
  [EAutomationRunFamily.WEBHOOK]: EContentReportTargetKind.WEBHOOK_RUN,
  [EAutomationRunFamily.HOOK]: EContentReportTargetKind.HOOK_RUN,
};

// NOTE: NSAI-5752 — the ONE place a failed report turns into something to show
// a person. Shared so web and mobile cannot drift into two different answers to
// "you already reported this", and so neither has to parse a localized string
// off the wire to tell a handled refusal from a real failure.
//
// Keys are relative to the `contentReport` namespace, which both clients load
// with useTranslation("contentReport") — so the caller passes the result to `t`
// unchanged.
//
// Unknown codes and transport errors both fall through to the generic key on
// purpose: a reporter is not the right audience for a distinction they cannot
// act on, and the alternative is a raw server message in whatever language the
// server happened to answer in.
// NOTE: A literal union, not `string`. i18next types `t` against the loaded
// resource bundle, so a widened return here is rejected at every call site —
// which is the type system doing its job: it means a key that does not exist in
// content-report.json cannot reach `t` in the first place.
export type TContentReportErrorKey =
  | "errors.alreadyReported"
  | "errors.targetNotFound"
  | "errors.notReportable"
  | "errors.generic";

const ERROR_CODE_TO_TRANSLATION_KEY: Record<string, TContentReportErrorKey> = {
  [CONTENT_REPORT_ERROR_CODES.ALREADY_REPORTED]: "errors.alreadyReported",
  [CONTENT_REPORT_ERROR_CODES.TARGET_NOT_FOUND]: "errors.targetNotFound",
  [CONTENT_REPORT_ERROR_CODES.NOT_REPORTABLE]: "errors.notReportable",
};

const GENERIC_TRANSLATION_KEY: TContentReportErrorKey = "errors.generic";

// NOTE: The code rides in the exception's `message`, which is how every Nest
// HttpException on this platform carries it — the thrown string IS the code.
// Read defensively: this runs inside a catch, so it must not be the thing that
// throws.
//
// Narrowed with `isAxiosError` rather than asserted: both clients reach this
// endpoint through the shared axios client, and a cast here would claim a shape
// for anything a catch happens to receive — a DOMException, a string, an
// abort — which is exactly the case this has to survive.
export const resolveContentReportErrorKey = (
  error: unknown,
): TContentReportErrorKey => {
  if (!isAxiosError(error)) return GENERIC_TRANSLATION_KEY;

  const responseMessage: unknown = error.response?.data?.message;

  if (typeof responseMessage !== "string") return GENERIC_TRANSLATION_KEY;

  return (
    ERROR_CODE_TO_TRANSLATION_KEY[responseMessage] ?? GENERIC_TRANSLATION_KEY
  );
};

// NOTE: NSAI-5752 — the reason pickers on both clients hand back a plain string
// (Radix RadioGroup on web, the sheet's action key on mobile), and the form
// field is typed to the enum. Narrowing here rather than asserting means an
// unexpected value fails closed — the picker simply does not set it — instead of
// being waved through into a payload the server would then reject.
export const isContentReportReason = (
  value: string,
): value is EContentReportReason =>
  CONTENT_REPORT_REASONS.some((reason) => reason === value);

interface IHasReportableMeetingOutputProps {
  overview: string | null;
  notes: unknown;
  actionItems: unknown;
}

// NOTE: True when a value actually holds something, for the three shapes a
// meeting's AI fields arrive in: a plain string, a structured-note array, or a
// parsed object. Whitespace does not count as output.
//
// The `notes` key is unwrapped rather than counted, because that is what the
// screens do: `parseStructuredNotes` treats `{ notes: [...] }` as a wrapper and
// renders the array inside it. Counting the wrapper's own keys made
// `{ notes: [] }` look like output and offered Report on a meeting that renders
// nothing.
const hasValue = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (value !== null && typeof value === "object") {
    if ("notes" in value) return hasValue(value.notes);

    return Object.keys(value).length > 0;
  }

  return false;
};

// NOTE: NSAI-6374 — the second half of "may this meeting be reported", and the
// half the first implementation was missing. Status alone is not the question:
// a `failed` meeting is not "in progress", so an in-progress check let it
// through and offered Report on a screen that says notes, transcript and tasks
// are unavailable — and the server accepted that report. Found on iOS.
//
// The mirror case rules out fixing it with a status test: a `processed` meeting
// can carry no notes at all, and gating on PROCESSED would still offer Report
// on that empty screen. So the invariant is the content, not the lifecycle.
//
// Shared because web and mobile render the same control from the same fields,
// and both got this wrong in the same way.
export const hasReportableMeetingOutput = ({
  overview,
  notes,
  actionItems,
}: IHasReportableMeetingOutputProps): boolean =>
  hasValue(overview) || hasValue(notes) || hasValue(actionItems);
